import {createSlice, PayloadAction} from '@reduxjs/toolkit';
import _ from 'lodash';
import {Amount, CustomerVopChoice, FeesTypeEnum, StandingOrderPeriod, VopBeneficiaryStatus} from '../../../@types/PaymentProTypes';
import {AccountType, Beneficiary} from '../../../@types/BeneficiaryTypes';
import {Concat} from '../../../../@types/utils';
import {TransferToType} from './storeSinglePayment';

export enum StandingOrderToType {
  MY_ACCOUNTS = 'MY_ACCOUNTS',
  BENEFICIARY = 'BENEFICIARY'
}

export type StandingOrderState = {
  standingOrderData: CreateStandingOrder;
  step: number;
};

export type PrivateStandingOrderBeneficiary = {
  accountReference: string | null;
  contractReference: string | null;
  beneficiaryAccountType: AccountType;
};

export type UpdateStandingOrder = {
  contractReference: string | null,
  standingOrderReference: string | null,
  primaryContract: string | null,
  amount: number,
  communication: string | null,
  periodicity?: string |null,
  lastExecutionDate?: string |null,
  vop?: {
    uuid: string | null,
    status: VopBeneficiaryStatus | null,
    vopBeneficiaryName: string | null,
    choice: CustomerVopChoice | null,
    vopNameExtended?: string | null
  }
};

export type CreateStandingOrder = {
  firstExecutionDate: string | null,
  lastExecutionDate: string | null,
  periodicity: StandingOrderPeriod,

  contractReference: string | null,
  primaryContract: string | null,

  accountFrom: string,
  initiatorName?: string,
  beneficiary: PrivateStandingOrderBeneficiary | Beneficiary,

  communication: string | null,
  amount: Amount,
  accountCurrency?: string | null,
  regulatoryReportingReason: string | null,

  debitAdvice: boolean,
  saveBeneficiary: boolean,
  saveCommunication: boolean,
  transferToType: TransferToType,
  transferRequestFees: FeesTypeEnum | null,

  signatureRequest?: {
    challengeId: string,
    answer: string
  },

  vop?: {
    uuid: string | null,
    status: VopBeneficiaryStatus | null,
    vopBeneficiaryName: string | null,
    choice: CustomerVopChoice | null,
    vopNameExtended?: string | null
  }
};

export type StandingOrderResult = {
  message: string;
  preInstruction?: boolean,
  operationReference: string;
  status: string;
  warnings: { code: string, reason: string }[];
  errors: { code: string, reason: string }[];
};

export type FeesRequest = {
  amount: number,
  currency: string,
  countryOfDestination: string
};

export const daysOfMonthEnabled = [1, 5, 10, 15, 20, 25, 30];

const initialState: StandingOrderState = {
  standingOrderData: {
    firstExecutionDate: null,
    lastExecutionDate: null,
    periodicity: StandingOrderPeriod.MENSUEL,
    contractReference: null,
    primaryContract: null,

    accountFrom: '',
    beneficiary: {
      accountReference: null,
      contractReference: null,
      beneficiaryAccountType: AccountType.IBAN
    },

    communication: null,
    amount: {
      value: null,
      currency: 'EUR'
    },
    accountCurrency: null,
    regulatoryReportingReason: '',

    debitAdvice: true,
    saveBeneficiary: false,
    saveCommunication: false,
    transferToType: TransferToType.BGL_BGL,
    transferRequestFees: null
  },
  step: 1
};

export type CreateStandingOrderDataFieldType =
  Omit<keyof CreateStandingOrder, 'amount' | 'beneficiary'>
  | Concat<'amount.', keyof Amount>
  | Concat<'beneficiary.', keyof Beneficiary>;

const nullifEmpty = (val: string) => {
  return (val === null || /^\s*$/.test(String(val))) ? null : val;
};

const standingOrderSlice = createSlice({
  initialState,
  name: 'payment/singlePayment',
  reducers: {
    setStandingOrderData(state, action: PayloadAction<{ field: CreateStandingOrderDataFieldType, value: any }>) {
      if(action.payload.field === 'beneficiary.foreignNationalReference'){
        //Set to null if empty or only spaces to pass @Valid
        _.set(state.standingOrderData, action.payload.field as string, nullifEmpty(action.payload.value));
      } else {
        _.set(state.standingOrderData, action.payload.field as string, action.payload.value);
      }
    },
    setStandingOrderBeneficiaryData(state, action: PayloadAction<{ value: Beneficiary }>) {
      _.set(state.standingOrderData, 'beneficiary', action.payload.value);
    },
    setStandingOrderBeneficiaryFieldData(state, action: PayloadAction<{ field: keyof Beneficiary, value: string }>) {
      if(action.payload.field === 'foreignNationalReference'){
        _.set(state.standingOrderData, `beneficiary.${action.payload.field}`, nullifEmpty(action.payload.value));
      } else {
        _.set(state.standingOrderData, `beneficiary.${action.payload.field}`, action.payload.value);
      }
    },
    setStandingOrderStep(state, action: PayloadAction<number>) {
      state.step = action.payload;
    },
    setTransferToTypeSO(state, action: PayloadAction<TransferToType>) {
      _.set(state.standingOrderData, 'transferToType', action.payload);
    },
    resetBeneficiarySo(state) {
      _.set(state.standingOrderData, 'beneficiary', initialState.standingOrderData.beneficiary);
    },
    resetStandingOrderData(state) {
      state.standingOrderData = initialState.standingOrderData;
    }
  }
});

export const {
  setStandingOrderData,
  setStandingOrderBeneficiaryData,
  setStandingOrderBeneficiaryFieldData,
  setStandingOrderStep,
  setTransferToTypeSO,
  resetBeneficiarySo,
  resetStandingOrderData
} = standingOrderSlice.actions;

export const reducer = standingOrderSlice.reducer;
